import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { MaterialService } from '../../../../material.service';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialSearchData } from '../../../../../../models/data-structures/materials/material-search-data.model';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { FeedstockMaterial } from '../../../../../../models/data-structures/materials/materials-data.model';
import { AnessaTranslationService } from '../../../../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-feedstock-dropdowns-search',
  templateUrl: './feedstock-dropdowns-search.component.html',
  styleUrls: ['./feedstock-dropdowns-search.component.scss']
})
export class FeedstockDropdownsSearchComponent extends UserpilotReloader implements OnInit, OnDestroy {
  /** an notifier that will be triggered when this component is destroyed */
  onDestroyNotifier = new EventEmitter();
  /**
   * Trigger used to trigger translation change when the language is updated.
   */
  trigger: string;

  /** an array of MaterialSearchData that contains all material search data */
  searchData: MaterialSearchData[] = [];
  /** an array of MaterialSearchData that contains filtered material search data. Data is filtered based
   * on the current search criteria */
  filteredSearchData: MaterialSearchData[] = [];
  /** The currently selected MaterialSearchData */
  selectedMaterial: MaterialSearchData;
  /** The search criteria entered by the user */
  searchCriteria = '';
  /** a boolean that signifies if the components data has been loaded */
  dataLoaded = false;


  constructor(private materialService: MaterialService,
              private translationService: AnessaTranslationService,
              private dialogRef: MatDialogRef<FeedstockDropdownsSearchComponent>
  ) {
    super();
  }

  /**
   * sets up data subscriptions to load all data required by the component
   */
  ngOnInit(): void {
    this.materialService.feedstockMaterialsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.dataLoaded = true;
          this.populateSearchData(this.materialService.getFeedstockMaterials());
        }
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.trigger = language;
      });
  }

  /**
   * cleans up all subscriptions within the component; triggers onDestroyNotifier and unsubscribes from it
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Populates the material search data array with the provided FeedstockMaterial array
   * @param materials is used to populate the current material search data array
   */
  private populateSearchData(materials: FeedstockMaterial[]) {
    if (materials) {
      for (const material of materials) {
        const searchItem = new MaterialSearchData(
          material.id,
          material.category,
          material.type,
          material.source,
          material.catTranslationId,
          material.typeTranslationId,
          material.sourceTranslationId,
          );
        this.searchData.push(searchItem);
      }
      this.filteredSearchData = this.searchData;
    }
  }

  /**
   * closes the material search pop-up with the currently selected material
   * @param item is the selected MaterialSearchData item
   */
  onItemClick(item: MaterialSearchData) {
    this.dialogRef.close(item);
  }

  /**
   * Updates the filtered search data array.
   */
  onSearchChange() {
    // done in an async function call to avoid the delay caused by the function being processes
    const asyncFunction = () => {
      if (!this.searchData) {
        return;
      }
      let search = this.searchCriteria;
      if (!search) {
        this.filteredSearchData = this.searchData.slice();
        return;
      } else {
        search = search.toLowerCase();
      }

      this.filteredSearchData = this.searchData.filter(
        input => this.translationService.get(input.fullDisplayName).toLowerCase().indexOf(search) > -1
      );
    };
    asyncFunction();
  }

  /**
   * closes the material search pop-up with no data being returned
   */
  onCloseClick() {
    this.dialogRef.close();
  }
}
